/**
 * ProjectStatementTab — invoice balance statement for one project.
 *
 * Shows what was contracted (from the linked quotes), what has been invoiced,
 * any manual adjustments, and what remains — all ex-GST with GST and inc-GST
 * shown alongside.
 *
 * Only the adjustment lines are stored. Contract and invoiced figures are
 * recomputed from the live documents on every load, so the statement cannot go
 * stale against the quotes and invoices it summarises.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Plus, Trash2, FileText, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { API, apiErrorMessage } from '../../lib/api';
import { formatCurrency } from '../../lib/defaultData';
import { computeStatement, GST_RATE } from '../../lib/statementMath';

const SAVE_DEBOUNCE_MS = 1500;

const STATUS_STYLE = {
  draft: { bg: '#f3f4f6', color: '#6b7280' },
  sent: { bg: '#dbeafe', color: '#1e40af' },
  paid: { bg: '#d1fae5', color: '#065f46' },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.draft;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide"
          style={{ background: s.bg, color: s.color }}>
      {status || 'draft'}
    </span>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#c9a84c' }}>{label}</div>
      <div className="text-sm text-[#1a1a2e]">{value || '—'}</div>
    </div>
  );
}

/** One row of the totals panel. `strong` marks the balance line. */
function TotalRow({ label, ex, gst, inc, strong, muted }) {
  return (
    <div className="grid gap-2 px-3 py-2" style={{
      gridTemplateColumns: '1fr 120px 100px 120px',
      borderTop: '1px solid rgba(201,168,76,0.15)',
      background: strong ? 'rgba(201,168,76,0.08)' : 'transparent',
    }}>
      <span className={`text-sm ${strong ? 'font-semibold' : ''}`} style={{ color: muted ? '#9ca3af' : '#1a1a2e' }}>{label}</span>
      <span className={`text-sm font-mono text-right ${strong ? 'font-semibold' : ''}`}>{ex}</span>
      <span className="text-sm font-mono text-right" style={{ color: '#6b7280' }}>{gst}</span>
      <span className={`text-sm font-mono text-right ${strong ? 'font-semibold' : ''}`}>{inc}</span>
    </div>
  );
}

export default function ProjectStatementTab({ project }) {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const saveTimer = useRef(null);

  const projectId = project?.id;

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const [q, i, s] = await Promise.all([
          axios.get(`${API}/quotes`, { params: { project_id: projectId } }),
          axios.get(`${API}/invoices`, { params: { project_id: projectId } }),
          axios.get(`${API}/projects/${projectId}/statement`),
        ]);
        if (cancelled) return;
        // The list endpoints return trimmed projections; the money fields we
        // need (categories / items) only come back on the full document.
        const fullQuotes = await Promise.all(
          (q.data?.data || []).map(row => axios.get(`${API}/quotes/${row.id}`).then(r => r.data).catch(() => null))
        );
        const fullInvoices = await Promise.all(
          (i.data?.data || []).map(row => axios.get(`${API}/invoices/${row.id}`).then(r => r.data).catch(() => null))
        );
        if (cancelled) return;
        setQuotes(fullQuotes.filter(Boolean));
        setInvoices(fullInvoices.filter(Boolean));
        setAdjustments(s.data?.adjustments || []);
      } catch (err) {
        console.error('Failed to load statement:', err);
        toast.error(apiErrorMessage(err, 'Could not load the statement.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  useEffect(() => {
    if (project?.client_id) {
      axios.get(`${API}/clients/${project.client_id}`)
        .then(r => setClient(r.data)).catch(() => {});
    }
  }, [project?.client_id]);

  const statement = useMemo(
    () => computeStatement({ quotes, invoices, adjustments }),
    [quotes, invoices, adjustments]
  );

  const persist = useCallback(async (next) => {
    setSaving(true);
    try {
      await axios.put(`${API}/projects/${projectId}/statement`, {
        title: 'Invoice Balance Statement',
        adjustments: next,
        notes: '',
      });
    } catch (err) {
      console.error('Statement save failed:', err);
      toast.error(apiErrorMessage(err, 'Could not save the adjustment.'));
    }
    setSaving(false);
  }, [projectId]);

  const scheduleSave = useCallback((next) => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persist(next), SAVE_DEBOUNCE_MS);
  }, [persist]);

  const updateAdjustments = useCallback((next) => {
    setAdjustments(next);
    scheduleSave(next);
  }, [scheduleSave]);

  const addAdjustment = () => updateAdjustments([
    ...adjustments,
    { id: crypto.randomUUID(), seq: adjustments.length, description: '', amount: 0 },
  ]);

  const editAdjustment = (id, field, value) => updateAdjustments(
    adjustments.map(a => (a.id === id ? { ...a, [field]: value } : a))
  );

  const removeAdjustment = (id) => updateAdjustments(adjustments.filter(a => a.id !== id));

  useEffect(() => () => clearTimeout(saveTimer.current), []);

  if (loading) {
    return (
      <div className="text-center py-16 text-[#6b7280]">
        <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin opacity-40" />
        Loading statement…
      </div>
    );
  }

  const fc = (n) => (n === null || n === undefined ? '—' : formatCurrency(n));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-[#1a1a2e]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Invoice Balance Statement
        </h3>
        <span className="text-xs" style={{ color: saving ? '#c9a84c' : '#9ca3af' }}>
          {saving ? 'Saving…' : 'Adjustments save automatically'}
        </span>
      </div>

      {/* Client + project */}
      <div className="grid grid-cols-2 gap-4 p-4 rounded-md" style={{ background: '#f8f7f4', border: '1px solid #e5e4df' }}>
        <div className="space-y-2">
          <Field label="Client" value={project?.client_name} />
          <Field label="ABN" value={client?.abn} />
          <Field label="Address" value={client?.address} />
        </div>
        <div className="space-y-2">
          <Field label="Project" value={project?.project_name} />
          <Field label="Project No" value={project?.project_no} />
          <Field label="Site Address" value={project?.site_address} />
        </div>
      </div>

      {/* A missing quote must not read as a zero balance. */}
      {!statement.hasContract && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-md"
             style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.35)' }}>
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#c9a84c' }} />
          <span className="text-xs" style={{ color: '#1a1a2e' }}>
            No quotation is linked to this project, so there is no contract value to compare against —
            the balance below is blank rather than zero. Link a quote from the Quotes tab.
          </span>
        </div>
      )}

      {statement.overInvoiced && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-md"
             style={{ background: '#fee2e2', border: '1px solid #fca5a5' }}>
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#991b1b' }} />
          <span className="text-xs" style={{ color: '#991b1b' }}>
            Invoiced value exceeds the contract value. The balance below is negative.
          </span>
        </div>
      )}

      {/* Invoices */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#c9a84c' }}>
          Invoices ({statement.lines.length})
          {statement.draftCount > 0 && (
            <span className="ml-2 font-normal normal-case" style={{ color: '#9ca3af' }}>
              — includes {statement.draftCount} draft totalling {fc(statement.draftTotal)}
            </span>
          )}
        </div>
        {statement.lines.length === 0 ? (
          <div className="text-center py-8 rounded-md text-sm" style={{ background: '#f8f7f4', border: '1px solid #e5e4df', color: '#6b7280' }}>
            <FileText className="w-8 h-8 mx-auto mb-2" style={{ color: 'rgba(201,168,76,0.4)' }} />
            No invoices linked to this project yet
          </div>
        ) : (
          <div className="rounded-md overflow-hidden" style={{ border: '1px solid #e5e4df' }}>
            <div className="grid gap-2 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider"
                 style={{ gridTemplateColumns: '130px 100px 1fr 80px 120px', background: '#f8f7f4', color: '#6b7280' }}>
              <span>Invoice No</span><span>Date</span><span>Description</span><span>Status</span>
              <span className="text-right">Amount (ex GST)</span>
            </div>
            {statement.lines.map(l => (
              <div key={l.id}
                   className="grid gap-2 px-3 py-2 items-center cursor-pointer hover:bg-[#f3f2ef]"
                   style={{ gridTemplateColumns: '130px 100px 1fr 80px 120px', borderTop: '1px solid rgba(201,168,76,0.1)' }}
                   onClick={() => navigate(`/invoice/${l.id}?from_project=${projectId}`)}>
                <span className="font-mono text-sm font-semibold text-[#1a1a2e]">{l.invoice_no}</span>
                <span className="text-sm text-[#6b7280]">{l.date || '—'}</span>
                <span className="text-sm truncate">{l.description}</span>
                <span><StatusBadge status={l.status} /></span>
                <span className="text-sm font-mono text-right">{fc(l.amount)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Manual adjustments */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#c9a84c' }}>
            Adjustments
            <span className="ml-2 font-normal normal-case" style={{ color: '#9ca3af' }}>
              — negative for a discount. Adjusts the balance, not the contract value.
            </span>
          </div>
          <Button onClick={addAdjustment} size="sm" variant="outline">
            <Plus className="w-4 h-4 mr-1" /> Add line
          </Button>
        </div>
        {adjustments.length > 0 && (
          <div className="rounded-md overflow-hidden" style={{ border: '1px solid #e5e4df' }}>
            {adjustments.map(a => (
              <div key={a.id} className="grid gap-2 px-3 py-2 items-center"
                   style={{ gridTemplateColumns: '1fr 140px 40px', borderTop: '1px solid rgba(201,168,76,0.1)' }}>
                <input
                  value={a.description}
                  onChange={e => editAdjustment(a.id, 'description', e.target.value)}
                  placeholder="e.g. Goodwill discount"
                  className="px-2 py-1 border rounded text-sm"
                  style={{ borderColor: '#e5e4df' }}
                />
                <input
                  type="text"
                  inputMode="decimal"
                  value={a.amount}
                  onChange={e => {
                    const raw = e.target.value;
                    // Keep the raw string while typing so a lone "-" survives.
                    const n = raw === '' || raw === '-' ? raw : Number(raw);
                    editAdjustment(a.id, 'amount', Number.isNaN(n) ? a.amount : n);
                  }}
                  className="px-2 py-1 border rounded text-sm font-mono text-right"
                  style={{ borderColor: '#e5e4df' }}
                />
                <button onClick={() => removeAdjustment(a.id)}
                        className="p-1.5 rounded hover:bg-red-50 text-[#6b7280] hover:text-[#dc2626]">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Totals */}
      <div className="rounded-md overflow-hidden" style={{ border: '1px solid #e5e4df' }}>
        <div className="grid gap-2 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider"
             style={{ gridTemplateColumns: '1fr 120px 100px 120px', background: '#1a1a2e', color: '#c9a84c' }}>
          <span></span>
          <span className="text-right">Ex GST</span>
          <span className="text-right">GST {Math.round(GST_RATE * 100)}%</span>
          <span className="text-right">Inc GST</span>
        </div>
        <TotalRow label="Contract value (quoted)" ex={fc(statement.contract)} gst={fc(statement.contractGst)} inc={fc(statement.contractIncGst)} />
        <TotalRow label="Less invoiced to date" ex={fc(statement.invoiced)} gst={fc(statement.invoicedGst)} inc={fc(statement.invoicedIncGst)} />
        {adjustments.length > 0 && (
          <TotalRow label="Adjustments" ex={fc(statement.adjustments)} gst="" inc="" muted />
        )}
        <TotalRow label="Balance remaining" ex={fc(statement.balance)} gst={fc(statement.balanceGst)} inc={fc(statement.balanceIncGst)} strong />
      </div>
    </div>
  );
}
